/**
 * Forward migration of the high-score table. The first persisted shape had no `version` — a
 * bare `Record<mapId, runs[]>` in localStorage. That generation is v0; missing `version` is v0
 * so existing tables survive this bump losslessly.
 *
 * v1 rows were `{ timeMs, turns, recordedAt }`. v2 adds calculated boat time, speed/turn stats,
 * and an optional replay. v3 stores tightest turn as degrees (`tightestTurnDeg`); a v2 radius
 * in metres cannot be recovered as an angle, so it becomes `0`. Old rows keep their realtime
 * and assume they finished at the end of the last turn (`calculatedTimeMs = turns × 1000`).
 * v4 rescales those 1 s/turn boat times and km/h stats to `TURN_DURATION_S` so a pre-v4 run
 * still ranks against a new race of the same path.
 * v5 stores the hull id, name, length, beam, livery, and turn / accelerate / brake. Older
 * runs were the hard-coded hull, so a missing boat is the factory Skiff. A missing
 * planing speed on that hull is the skiff's 45. The table is not cleared.
 */

import { TURN_DURATION_S } from '../constants.ts';
import { migrateHighScoreBoat } from './boat.ts';
import {
  CURRENT_HIGH_SCORE_VERSION,
  OLDEST_SUPPORTED_HIGH_SCORE_VERSION,
  type HighScore,
  type HighScoreDocument,
  type RaceReplay,
} from './types.ts';

/** v0–v3 stored boat time and km/h as if a turn lasted one second. */
const PRE_V4_TURN_DURATION_S = 1;
/** First version whose stored times and km/h already use `TURN_DURATION_S`. */
const HIGH_SCORE_TURN_DURATION_VERSION = 4;

export { CURRENT_HIGH_SCORE_VERSION, OLDEST_SUPPORTED_HIGH_SCORE_VERSION };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function migrateVec2(value: unknown): { x: number; y: number } | null {
  if (!isRecord(value)) {
    return null;
  }

  const x = asFiniteNumber(value.x);
  const y = asFiniteNumber(value.y);

  if (x === null || y === null) {
    return null;
  }

  return { x, y };
}

function migrateReplay(value: unknown): RaceReplay | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (!isRecord(value) || !isRecord(value.start) || !Array.isArray(value.moves)) {
    return null;
  }

  const position = migrateVec2(value.start.position);
  const heading = asFiniteNumber(value.start.heading);

  if (position === null || heading === null) {
    return null;
  }

  const moves: RaceReplay['moves'] = [];

  for (const move of value.moves) {
    if (!isRecord(move)) {
      return null;
    }

    const destination = migrateVec2(move.destination);
    const radius = asFiniteNumber(move.radius);

    if (destination === null || radius === null) {
      return null;
    }

    moves.push({ destination, radius });
  }

  return { start: { position, heading }, moves };
}

function migrateScore(value: unknown): HighScore | null {
  if (!isRecord(value)) {
    return null;
  }

  const recordedAt = asFiniteNumber(value.recordedAt);
  const timeMs = asFiniteNumber(value.timeMs);

  if (recordedAt === null || timeMs === null) {
    return null;
  }

  const rounds = asFiniteNumber(value.rounds) ?? asFiniteNumber(value.turns);

  if (rounds === null) {
    return null;
  }

  const calculatedTimeMs = asFiniteNumber(value.calculatedTimeMs) ?? rounds * 1000;

  return {
    rounds,
    calculatedTimeMs,
    timeMs,
    maxSpeedKmh: asFiniteNumber(value.maxSpeedKmh) ?? 0,
    averageSpeedKmh: asFiniteNumber(value.averageSpeedKmh) ?? 0,
    tightestTurnDeg: asFiniteNumber(value.tightestTurnDeg) ?? 0,
    recordedAt,
    replay: migrateReplay(value.replay),
    boat: migrateHighScoreBoat(value.boat),
  };
}

function scoresFromTable(value: Record<string, unknown>): Record<string, HighScore[]> {
  const scores: Record<string, HighScore[]> = {};

  for (const [mapId, runs] of Object.entries(value)) {
    if (mapId === 'version' || mapId === 'scores' || !Array.isArray(runs)) {
      continue;
    }

    scores[mapId] = runs.flatMap((run) => {
      const score = migrateScore(run);
      return score ? [score] : [];
    });
  }

  return scores;
}

function scoresFromWrapped(value: unknown): Record<string, HighScore[]> | null {
  if (!isRecord(value)) {
    return null;
  }

  const scores: Record<string, HighScore[]> = {};

  for (const [mapId, runs] of Object.entries(value)) {
    if (!Array.isArray(runs)) {
      continue;
    }

    scores[mapId] = runs.flatMap((run) => {
      const score = migrateScore(run);
      return score ? [score] : [];
    });
  }

  return scores;
}

function scalePreV4Score(score: HighScore): HighScore {
  const timeScale = TURN_DURATION_S / PRE_V4_TURN_DURATION_S;

  return {
    ...score,
    calculatedTimeMs: score.calculatedTimeMs * timeScale,
    maxSpeedKmh: score.maxSpeedKmh / timeScale,
    averageSpeedKmh: score.averageSpeedKmh / timeScale,
  };
}

function scalePreV4Table(scores: Record<string, HighScore[]>): Record<string, HighScore[]> {
  const scaled: Record<string, HighScore[]> = {};

  for (const [mapId, runs] of Object.entries(scores)) {
    scaled[mapId] = runs.map(scalePreV4Score);
  }

  return scaled;
}

function scoresForVersion(
  scores: Record<string, HighScore[]>,
  rawVersion: number | undefined,
): Record<string, HighScore[]> {
  if (rawVersion === undefined || rawVersion < HIGH_SCORE_TURN_DURATION_VERSION) {
    return scalePreV4Table(scores);
  }

  return scores;
}

export function createEmptyHighScoreDocument(): HighScoreDocument {
  return {
    version: CURRENT_HIGH_SCORE_VERSION,
    scores: {},
  };
}

/**
 * Normalises any supported generation into a current `HighScoreDocument`, or returns null if the
 * input is not a score table. Unknown fields are dropped; bad rows are skipped.
 */
export function migrateHighScores(value: unknown): HighScoreDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const rawVersion = value.version;

  if (rawVersion === undefined) {
    return {
      version: CURRENT_HIGH_SCORE_VERSION,
      scores: scoresForVersion(scoresFromTable(value), rawVersion),
    };
  }

  if (
    typeof rawVersion !== 'number' ||
    rawVersion < OLDEST_SUPPORTED_HIGH_SCORE_VERSION ||
    rawVersion > CURRENT_HIGH_SCORE_VERSION
  ) {
    return null;
  }

  if (rawVersion === 0) {
    const wrapped = isRecord(value.scores) ? scoresFromWrapped(value.scores) : null;

    return {
      version: CURRENT_HIGH_SCORE_VERSION,
      scores: scoresForVersion(wrapped ?? scoresFromTable(value), rawVersion),
    };
  }

  const scores = scoresFromWrapped(value.scores);

  if (scores === null) {
    return null;
  }

  return {
    version: CURRENT_HIGH_SCORE_VERSION,
    scores: scoresForVersion(scores, rawVersion),
  };
}
